<template>
  <div class="v2-buying-summary-store">
    <div
      v-if="showStoreHeader"
      class="v2-buying-summary-store__header"
    >
      <div
        class="v2-buying-summary-store__name"
        @click="handleAccordionClick"
      >
        {{ cart.storeName }}
      </div>
      <button
        class="v2-buying-summary-store__chevron"
        @click="handleAccordionClick"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          :style="{ transform: buyingSummaryTableVisible ? 'rotate(180deg)' : 'rotate(0deg)' }"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div class="v2-buying-summary-store__badges">
        <v2-badge variant="default">
          Total units: {{ formatNumber(cartValue.totalUnits) || 0 }}
        </v2-badge>
        <v2-badge variant="default">
          Total cost: {{ formatCurrency(Number(cartValue.totalValues), cartValue.currency) }}
        </v2-badge>
        <v2-badge variant="default">
          Total RRP: {{ formatCurrency(Number(cartValue.totalRRP), cartValue.currency) }}
        </v2-badge>
      </div>
    </div>
    <div
      v-if="(!showStoreHeader || buyingSummaryTableVisible) && tableFields.length"
      class="v2-buying-summary-store__table-wrapper"
    >
      <table class="v2-table">
        <thead>
          <tr>
            <th
              v-for="col in headings"
              :key="col.key"
              :class="{ 'v2-table__sortable': col.sortable }"
              @click="col.sortable ? handleSort(col.key) : null"
            >
              {{ col.key }}
              <span
                v-if="col.sortable && sortBy === col.key"
                class="v2-table__sort-icon"
              >
                {{ sortDesc ? '&#9660;' : '&#9650;' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, idx) in sortedItems"
            :key="item.uniqKey || idx"
          >
            <td>{{ item.Department }}</td>
            <td>{{ item.Category }}</td>
            <td>{{ item.SubCategory }}</td>
            <td>{{ item.Gender }}</td>
            <td>{{ item.OptionCount }}</td>
            <td>{{ item.Pieces ? formatNumber(item.Pieces) : 0 }}</td>
            <td>
              <span v-if="item">{{ formatCurrency(Number(item.TotalCost), item.currency) }}</span>
            </td>
            <td>
              <span v-if="item">{{ formatCurrency(Number(item.RRPTotal), item.currency) }}</span>
            </td>
            <td>
              <input
                v-if="item.OptionCount"
                v-model.number="item.Budget"
                type="number"
                class="v2-buying-summary-store__budget-input"
                min="0"
                @input="updateVariance(item)"
              >
            </td>
            <td>
              <v2-badge
                v-if="item.Variance"
                :variant="parseFloat(item.Variance) > 0 ? 'destructive' : 'success'"
              >
                {{ item.Variance }}
              </v2-badge>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { reactive } from '@vue/composition-api'
import { utils, constants as c, productUtil } from '@kingpin-global/kingpin-utils-frontend'
import { formatNumber } from '@/@core/utils/utils'
import { BUYING_SUMMARY_TABLE_FIELDS } from '@/table-columns-constants'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { debounce } from 'lodash'
import V2Badge from '@/components/v2/V2Badge.vue'
import { getCategorisedProduct } from '@/views/apps/checkout-v2/buying-summary-utils'

const { getProductAndCartTotals } = productUtil
const { formatCurrency } = utils
const { CURRENCY_CONVERSIONS } = c

export default {
  name: 'V2BuyingSummaryStore',
  components: {
    V2Badge,
  },
  props: {
    cart: {
      type: Object,
      required: true,
    },
    cartValue: {
      type: Object,
      required: true,
      default: () => ({
        totalValues: 0,
        totalUnits: 0,
        totalRRP: 0,
        currency: CURRENCY_CONVERSIONS.USD.SYMBOL,
      }),
    },
    setTableItems: {
      type: Function,
      default: () => {},
    },
    tableFields: {
      type: Array,
      default: () => [],
    },
    showStoreHeader: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      buyingSummaryTableVisible: true,
      headings: BUYING_SUMMARY_TABLE_FIELDS(),
      sortBy: '',
      sortDesc: false,
    }
  },
  computed: {
    sortedItems() {
      if (!this.sortBy) return this.tableFields
      const items = [...this.tableFields]
      const dir = this.sortDesc ? -1 : 1
      items.sort((a, b) => dir * this.compareValues(a, b))
      return items
    },
  },
  methods: {
    compareValues(a, b) {
      const aVal = a[this.sortBy]
      const bVal = b[this.sortBy]
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return aVal - bVal
      }
      const aStr = String(aVal || '').toLowerCase()
      const bStr = String(bVal || '').toLowerCase()
      if (aStr < bStr) return -1
      if (aStr > bStr) return 1
      return 0
    },
    handleAccordionClick() {
      this.buyingSummaryTableVisible = !this.buyingSummaryTableVisible
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_TOGGLES_STORE_ACCORDION_BUYING_SUMMARY, {
        cart: this.cart,
      })
    },
    handleSort(key) {
      if (this.sortBy === key) {
        this.sortDesc = !this.sortDesc
      } else {
        this.sortBy = key
        this.sortDesc = false
      }
      this.trackTableSort({ sortBy: key, sortDesc: this.sortDesc })
    },
    trackTableSort(props) {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SORTS_ITEMS_BUYING_SUMMARY, {
        cart: this.cart,
        columnName: props?.sortBy,
        order: props?.sortDesc ? 'Descending' : 'Ascending',
      })
    },
  },
  setup(props) {
    const tableItems = reactive([])
    const products = props.cart.products
    getProductAndCartTotals(products)

    // categorising each products
    const categorisedProduct = getCategorisedProduct(products)

    // Assign products value to TableItems
    const keys = Object.keys(categorisedProduct)
    if (Array.isArray(keys)) {
      keys.forEach(key => {
        tableItems.push(categorisedProduct[key])
      })
    }
    props.setTableItems(tableItems)

    // Update Variance on Input
    const updateVariance = item => {
      let variance = 0
      if (item.Budget > 0) {
        variance = (((item.TotalCost / item.Budget) - 1) * 100).toFixed(2)
      }
      item.Variance = `${variance}%`
      debouncedTrackBudget(props.cart, item.Budget, variance)
    }

    const debouncedTrackBudget = debounce((cart, budget, variance) => {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CHANGES_BUDGET_BUYING_SUMMARY, {
        cart,
        budget,
        variance,
      })
    }, 500)

    return {
      products,
      updateVariance,
      formatNumber,
      formatCurrency,
    }
  },
}
</script>

<style lang="scss" scoped>
.v2-buying-summary-store {
  margin: var(--v2-space-1) 0;

  &__header {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
  }

  &__name {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--v2-foreground);
    cursor: pointer;
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--v2-foreground);
    padding: 0;

    svg {
      transition: transform 0.2s ease;
    }
  }

  &__badges {
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
    flex-wrap: wrap;
  }

  &__table-wrapper {
    margin-top: var(--v2-space-3);
    // No overflow here — an overflow container at this level would create
    // a new scrolling context and break the table-header sticky pinning
    // against the modal body. Horizontal overflow is handled by the modal
    // body in V2BuyingSummaryModal instead.
  }

  // Glass budget input — matches V2Input recipe.
  &__budget-input {
    width: 100px;
    padding: var(--v2-space-1) var(--v2-space-2);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-sm);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:focus) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }

    /* Hide number input spinners */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
  }
}

.v2-table {
  width: 100%;
  border-collapse: separate; // sticky thead needs separate borders so the
  border-spacing: 0;         // header's border-bottom doesn't disappear
  font-size: 0.875rem;

  th {
    text-align: left;
    padding: var(--v2-space-3) var(--v2-space-4);
    font-weight: 500;
    color: var(--v2-muted-foreground);
    background: var(--v2-muted);
    border-bottom: 1px solid var(--v2-border);
    white-space: nowrap;
    // Pin header to the modal-body scroll container. top: 0 works because
    // the parent (.v2-modal__body) is the nearest scrolling ancestor.
    position: sticky;
    top: 0;
    z-index: 2;
  }

  td {
    padding: var(--v2-space-3) var(--v2-space-4);
    border-bottom: 1px solid var(--v2-border);
    color: var(--v2-foreground);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: var(--v2-muted);
  }

  &__sortable {
    cursor: pointer;
    user-select: none;

    &:hover {
      color: var(--v2-foreground);
    }
  }

  &__sort-icon {
    font-size: 0.625rem;
    margin-left: var(--v2-space-1);
  }
}
</style>
